import { useCallback, useEffect, useRef, useState } from 'react';
import { configService } from '@/shared/lib/configService';

const POLL_INTERVAL_MS = 10_000; // check every 10 seconds

const ACTIVITY_EVENTS = [
  'mousemove',
  'mousedown',
  'keydown',
  'scroll',
  'touchstart',
  'click',
] as const;

/**
 * Tracks document focus and user inactivity without setTimeout.
 * Shows `showDialog` when:
 *  - The user returns to the page after threshold away (blur/visibility), OR
 *  - The user has been on the page but idle for threshold with no interaction, OR
 *  - The user navigates via back/forward button without activity after threshold.
 * The dialog must be dismissed manually via `dismissDialog`.
 *
 * @param onNavigationAttemptDuringInactivity - Optional callback fired when user tries to navigate during inactivity
 */
export const useDocumentFocus = (
  onNavigationAttemptDuringInactivity?: () => void
) => {
  const [showDialog, setShowDialog] = useState(false);
  const blurTimeRef = useRef<number | null>(null);
  const lastActivityRef = useRef<number>(0);
  const isPageVisibleRef = useRef<boolean>(true);
  const currentLocationRef = useRef<string>(window.location.pathname);
  const inactivityThresholdMs = configService.getSessionInactivityMs();

  const recordActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
  }, []);

  const markBlur = useCallback(() => {
    if (blurTimeRef.current === null) {
      blurTimeRef.current = Date.now();
    }
    isPageVisibleRef.current = false;
  }, []);

  const markFocus = useCallback(() => {
    if (blurTimeRef.current !== null) {
      const elapsed = Date.now() - blurTimeRef.current;
      if (elapsed > inactivityThresholdMs) {
        setShowDialog(true);
      }
      blurTimeRef.current = null;
    }
    isPageVisibleRef.current = true;
  }, [inactivityThresholdMs]);

  const checkInactivityThreshold = useCallback(() => {
    const idle = Date.now() - lastActivityRef.current;
    if (idle > inactivityThresholdMs) {
      setShowDialog(true);
      return true; // Inactivity detected
    }
    return false;
  }, [inactivityThresholdMs]);

  useEffect(() => {
    // Set the initial activity timestamp here to avoid calling Date.now() during render
    lastActivityRef.current = Date.now();

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        markBlur();
      } else {
        markFocus();
      }
    };

    const handlePopState = () => {
      // User clicked back/forward button - check inactivity
      const inactivityDetected = checkInactivityThreshold();
      if (inactivityDetected) {
        // Call the navigation attempt callback if provided
        if (onNavigationAttemptDuringInactivity) {
          onNavigationAttemptDuringInactivity();
        }
        // Prevent navigation by using browser history API
        setTimeout(() => {
          window.history.replaceState(null, '', currentLocationRef.current);
        }, 0);
      }
    };

    const handlePageShow = () => {
      // Page became visible (e.g., from back/forward cache) - check inactivity
      const inactivityDetected = checkInactivityThreshold();
      if (inactivityDetected) {
        // Call the navigation attempt callback if provided
        if (onNavigationAttemptDuringInactivity) {
          onNavigationAttemptDuringInactivity();
        }
        // Prevent navigation by using browser history API
        setTimeout(() => {
          window.history.replaceState(null, '', currentLocationRef.current);
        }, 0);
      }
    };

    const handleLocationChange = () => {
      // Update stored location when user navigates
      currentLocationRef.current = window.location.pathname;
    };

    window.addEventListener('blur', markBlur);
    window.addEventListener('focus', markFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    document.addEventListener('mouseleave', markBlur);
    document.addEventListener('mouseenter', markFocus);
    window.addEventListener('popstate', handlePopState);
    window.addEventListener('pageshow', handlePageShow);
    window.addEventListener('hashchange', handleLocationChange);

    ACTIVITY_EVENTS.forEach((event) =>
      document.addEventListener(event, recordActivity)
    );

    // Poll for inactivity using setInterval instead of setTimeout
    const intervalId = setInterval(() => {
      if (!isPageVisibleRef.current) return;
      const idle = Date.now() - lastActivityRef.current;
      if (idle > inactivityThresholdMs) {
        setShowDialog(true);
      }
    }, POLL_INTERVAL_MS);

    return () => {
      window.removeEventListener('blur', markBlur);
      window.removeEventListener('focus', markFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      document.removeEventListener('mouseleave', markBlur);
      document.removeEventListener('mouseenter', markFocus);
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('pageshow', handlePageShow);
      window.removeEventListener('hashchange', handleLocationChange);
      ACTIVITY_EVENTS.forEach((event) =>
        document.removeEventListener(event, recordActivity)
      );
      clearInterval(intervalId);
    };
  }, [
    markBlur,
    markFocus,
    recordActivity,
    checkInactivityThreshold,
    onNavigationAttemptDuringInactivity,
    inactivityThresholdMs,
  ]);

  const dismissDialog = useCallback(() => {
    setShowDialog(false);
    lastActivityRef.current = Date.now();
  }, []);

  return { showDialog, dismissDialog };
};
